import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Trophy, ShieldCheck, Sparkles, Factory, Users, Shirt, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import PageMeta from "../components/PageMeta";

export default function Impact() {
    const [stats, setStats] = useState({ delivered: 0, in_production: 0, clubs: 0, sponsors: 0, total_collected: 0 });
    const [testimonials, setTestimonials] = useState(TESTIMONIALS);

    useEffect(() => {
        api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/testimonials", { params: { limit: 3 } })
            .then((r) => {
                const items = r?.data?.items || [];
                if (items.length > 0) setTestimonials(items);
            })
            .catch(() => {});
    }, []);

    return (
        <div className="bg-[#09090b] text-zinc-100">
            <PageMeta
                title="Notre impact — Sponsormania.fr | Témoignages clubs & sponsors"
                description="Découvrez l'impact concret de Sponsormania : maillots livrés, clubs financés, sponsors engagés. Témoignages d'entraîneurs et de présidents de clubs amateurs."
            />
            {/* HERO */}
            <section className="border-b border-zinc-900 px-6 lg:px-8 pt-20 pb-24">
                <div className="max-w-7xl mx-auto">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-4">
                        Notre impact
                    </div>
                    <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase leading-[0.9] tracking-tighter max-w-5xl">
                        0% de commission.
                        <br />
                        <span className="text-orange-500">100% pour le sport amateur.</span>
                    </h1>
                    <p className="mt-8 max-w-3xl text-lg text-zinc-300">
                        Sponsormania.fr redistribue chaque euro collecté aux clubs amateurs. Pas d'intermédiaire,
                        pas de marge cachée — juste des maillots qui partent fièrement sur les terrains.
                    </p>
                </div>
            </section>

            {/* COMPTEURS */}
            <section className="px-6 lg:px-8 py-20 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto">
                    <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight mb-10">
                        Le mouvement en chiffres
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
                        <Counter label="Clubs financés" value={stats.clubs} testid="impact-clubs" />
                        <Counter label="Sponsors actifs" value={stats.sponsors} testid="impact-sponsors" />
                        <Counter label="Maillots livrés" value={stats.delivered} testid="impact-delivered" />
                        <Counter label="Collecté pour le sport" value={`${stats.total_collected.toLocaleString("fr-FR")}€`} testid="impact-collected" />
                    </div>
                </div>
            </section>

            {/* COMMENT ÇA MARCHE */}
            <section className="px-6 lg:px-8 py-20 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-3">
                        Comment ça marche
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight mb-10">
                        Trois étapes, une équipe.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                        {[
                            { n: "01", t: "Le club s'inscrit", d: "Inscription gratuite, validation rapide par notre équipe.", icon: <Users />, cls: "md:col-span-5" },
                            { n: "02", t: "Les sponsors choisissent", d: "Sélection visuelle des emplacements (poitrine, dos, épaules) et upload du logo HD.", icon: <Trophy />, cls: "md:col-span-7" },
                            { n: "03", t: "Production dès 19€", d: "Une fois le seuil atteint et la sélection validée, le maillot part en fabrication.", icon: <Factory />, cls: "md:col-span-12" },
                        ].map((s) => (
                            <div key={s.n} className={`card-tu ${s.cls} flex gap-6 items-start`}>
                                <div className="text-orange-500">{s.icon}</div>
                                <div>
                                    <div className="font-display text-5xl text-zinc-700 leading-none">{s.n}</div>
                                    <h3 className="font-display text-3xl tracking-tight uppercase mt-2">{s.t}</h3>
                                    <p className="text-zinc-400 mt-2">{s.d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TÉMOIGNAGES */}
            <section className="px-6 lg:px-8 py-20 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-3">
                        Ils nous font confiance
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight mb-10">
                        Témoignages
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {testimonials.map((t, i) => (
                            <figure key={t.id || t.author || i} className="card-tu" data-testid={`testimonial-${i}`}>
                                <blockquote className="text-zinc-200 text-lg leading-snug">"{t.quote}"</blockquote>
                                <figcaption className="mt-6">
                                    <div className="font-bold uppercase tracking-wider text-sm">{t.author}</div>
                                    <div className="text-xs text-zinc-500 uppercase tracking-widest">{t.role}</div>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* ENGAGEMENT */}
            <section className="px-6 lg:px-8 py-20 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
                    {COMMITMENTS.map((c) => (
                        <div key={c.title} className="card-tu">
                            <div className="text-orange-500 mb-3">{c.icon}</div>
                            <h3 className="font-display text-2xl uppercase tracking-tight">{c.title}</h3>
                            <p className="text-zinc-400 mt-2 text-sm">{c.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* PANTHER PARTNER */}
            <section
                data-testid="impact-panther-block"
                className="px-6 lg:px-8 py-20 border-b border-zinc-900"
            >
                <div className="max-w-7xl mx-auto">
                    <div className="card-tu border-orange-500/20 flex flex-col lg:flex-row lg:items-center gap-6">
                        <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center">
                            <Shirt size={30} className="text-white" />
                        </div>
                        <div className="flex-1">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-1">
                                Notre partenaire fabricant
                            </div>
                            <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight">
                                <span className="text-orange-500">Panther</span> fabrique chacun de nos maillots
                            </h3>
                            <p className="text-zinc-400 mt-3 max-w-2xl">
                                Marque française experte du textile sportif : impression HD,
                                fabrication à la demande, zéro invendu. Une garantie de qualité
                                pour chaque équipe amateur soutenue sur Sponsormania.fr.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/panther"
                                data-testid="impact-panther-internal"
                                className="btn-ghost whitespace-nowrap"
                            >
                                Découvrir Panther <ArrowRight size={16} />
                            </Link>
                            <a
                                href="https://www.mypanther.fr"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="impact-panther-external"
                                className="btn-primary whitespace-nowrap"
                            >
                                mypanther.fr ↗
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 lg:px-8 py-24">
                <div className="max-w-7xl mx-auto text-center">
                    <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tight">
                        Rejoignez le mouvement.
                    </h2>
                    <p className="mt-4 text-zinc-400 max-w-2xl mx-auto">
                        Inscrivez votre club ou devenez sponsor en moins de 2 minutes.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link to="/register" data-testid="impact-cta-club" className="btn-primary">
                            <Heart size={18} /> Inscrire mon club
                        </Link>
                        <Link to="/clubs" data-testid="impact-cta-sponsor" className="btn-ghost">
                            <Trophy size={18} /> Devenir sponsor
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Counter({ label, value, testid }) {
    return (
        <div className="bg-[#09090b] p-8" data-testid={testid}>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-2">{label}</div>
            <div className="font-display text-5xl md:text-6xl text-orange-500 leading-none">{value}</div>
        </div>
    );
}

const TESTIMONIALS = [
    {
        quote: "Trois sponsors locaux ont financé nos maillots U13 en 10 jours. Une émotion incroyable pour nos joueurs.",
        author: "Karim B.",
        role: "Président · FC Lyon Étoile",
    },
    {
        quote: "Notre logo sur le dos d'une équipe de quartier, ça vaut tous les panneaux publicitaires.",
        author: "Stéphanie M.",
        role: "Boulangerie locale, sponsor",
    },
    {
        quote: "Zéro paperasse, zéro commission. Sponsormania.fr nous a fait gagner un trimestre de démarchage.",
        author: "Coach Léo",
        role: "Handball Nantes Loire",
    },
];

const COMMITMENTS = [
    { title: "Textile HD", desc: "Impression Haute Définition, textile sportif.", icon: <ShieldCheck size={28} /> },
    { title: "0% commission", desc: "Chaque euro versé par les sponsors finance des maillots, point.", icon: <Sparkles size={28} /> },
    { title: "Sport pour tous", desc: "Tous les sports, tous les niveaux, du U7 au senior loisir.", icon: <Heart size={28} /> },
];
